// «Quando succede» con Gmail (tela, artboard 49): cosa fa partire il giro, quali email, quante
// per giro e per quanto PopTown ricorda quelle già fatte. La casella non si tocca. Dalla 0.1.40 la
// stessa connessione Google fa partire anche un promemoria di calendario (ConditionCalendar).
import type { ReactNode } from 'react';
import { Banner } from '../../components/Banner';
import { Field, TextField } from '../../components/Field';
import { Select } from '../../components/Select';
import type { RoutineCondition } from '../../lib/api';
import { Choice } from './Choice';
import { CalendarFields } from './ConditionCalendar';

const SHORTCUTS: [string, string][] = [
  ['non lette', 'is:unread in:inbox'],
  ['nuove di oggi', 'in:inbox newer_than:1d'],
  ['tutte le nuove', 'in:inbox'],
];
const DAYS = [1, 3, 7, 14, 30];

export function GmailFields({
  value,
  set,
  everySelect,
  calendar = false,
}: {
  value: RoutineCondition;
  set: (patch: Partial<RoutineCondition>) => void;
  everySelect: ReactNode;
  /** la connessione ha il calendario (ricollegata dopo la 0.1.40) */
  calendar?: boolean;
}) {
  const reminder = value.source === 'calendar';
  const byLabel = !reminder && value.label != null;
  const pill = (on: boolean) => (on ? 'rp-pill' : 'rp-pill rp-pill-off');
  return (
    <div className="rp-tool-box">
      <div style={{ display: 'grid', gap: 6 }}>
        <span className="t-strong">Cosa la fa partire</span>
        <div role="radiogroup" aria-label="Cosa la fa partire" className="rp-pills">
          <button
            type="button"
            role="radio"
            aria-checked={!reminder && !byLabel}
            className={pill(!reminder && !byLabel)}
            onClick={() => set({ source: 'gmail', label: null })}
          >
            Email nuove
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={byLabel}
            className={pill(byLabel)}
            onClick={() => set({ source: 'gmail', label: value.label ?? '' })}
          >
            Email con un’etichetta
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={reminder}
            className={pill(reminder)}
            disabled={!calendar}
            title={calendar ? undefined : 'Ricollega la connessione da Strumenti per il calendario'}
            onClick={() =>
              set({ source: 'calendar', label: null, minutes_before: value.minutes_before ?? 15 })
            }
          >
            Promemoria di calendario
          </button>
        </div>
        {!calendar && (
          <span className="t-caption dim">
            Per il calendario ricollega una volta la connessione da Strumenti: Google chiede il
            permesso nuovo.
          </span>
        )}
      </div>
      {reminder ? (
        <CalendarFields value={value} set={set} everySelect={everySelect} />
      ) : (
        <GmailRest value={value} set={set} everySelect={everySelect} byLabel={byLabel} />
      )}
    </div>
  );
}

function GmailRest({
  value,
  set,
  everySelect,
  byLabel,
}: {
  value: RoutineCondition;
  set: (patch: Partial<RoutineCondition>) => void;
  everySelect: ReactNode;
  byLabel: boolean;
}) {
  return (
    <>
      <div className="rw-grid">
        <Field label="Quali email" hint="Ricerca nella sintassi di Gmail, usata così com’è.">
          <TextField
            value={value.query ?? ''}
            onChange={(query) => set({ query })}
            placeholder="in:inbox"
            mono
          />
        </Field>
        {byLabel && (
          <Field label="Etichetta" hint="Come in Gmail, es. Fatture.">
            <TextField
              value={value.label ?? ''}
              onChange={(label) => set({ label })}
              invalid={!value.label?.trim()}
            />
          </Field>
        )}
        {everySelect}
      </div>
      <div className="t-caption dim">
        Scorciatoie:{' '}
        {SHORTCUTS.map(([name, q], i) => (
          <span key={q}>
            {i > 0 && ' · '}
            <button type="button" className="rp-link" onClick={() => set({ query: q })}>
              {name}
            </button>
          </span>
        ))}
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        <span className="t-strong">Giri</span>
        <div role="radiogroup" aria-label="Giri" className="rw-kinds">
          <Choice
            on={value.batch !== 'all'}
            title="Un giro per email"
            hint="Ogni email apre la sua sessione, con mittente, oggetto e testo. Le altre al controllo dopo, dalla più vecchia."
            onPick={() => set({ batch: 'each' })}
          />
          <Choice
            on={value.batch === 'all'}
            title="Un giro con tutte"
            hint="Una sessione con l’elenco delle nuove: il pop le legge con i suoi strumenti. Adatto a un riassunto."
            onPick={() => set({ batch: 'all' })}
          />
        </div>
      </div>
      <div className="rw-grid">
        <Field
          label="Ricorda le già fatte per"
          hint="Poi le dimentica. La casella non si tocca: niente etichette, niente «letto»."
        >
          <Select
            label="Per quanto ricorda le email già fatte"
            value={String(value.remember_days ?? 7)}
            onChange={(v) => set({ remember_days: Number(v) })}
            options={DAYS.map((n) => ({
              value: String(n),
              label: n === 1 ? '1 giorno' : `${n} giorni`,
            }))}
            width="100%"
          />
        </Field>
      </div>
      <Banner kind="info">
        <strong>Email come dato esterno.</strong> Il testo arriva al pop segnato come non fidato.
        Testo nascosto, commenti e caratteri invisibili si tolgono; le frasi rivolte a un modello si
        segnalano. Il pop non esegue istruzioni trovate in una email.
      </Banner>
    </>
  );
}
